// src/infra/adapters/iam/sessions.port-adapter.ts
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map, switchMap, tap } from 'rxjs/operators';
import { IamSdk } from 'lib-klab-sdks';
import { AppEventsService } from '../../events/products-events.service';
import { SessionEventsService } from '../../events/session-events.service';
import { KLAB_IAM_ENV } from '../../config/micros-env.tokens';
import { EnvMicroKlamIam } from '../../config/env.micro.klam-iam';
import { SessionsPort } from '../../../app/ports/iam/sessions.port';
import { RolesPort } from '../../../app/ports/iam/roles.port';
import { ROLES_PORT } from '../../../app/ports/iam/roles.token';
import {
    mapSDKAuthenticatedUserToDomain,
    mapSDKLoginToDomain,
    mapSDKMenuItemToDomain,
} from '../../mappers/iam/auth.mapper';
import { LoginRequest, LoginResponse, RolePermissions } from '../../../domain/models/session/session';
import { SessionCtl } from '../../services/session.ctl.service';
import { authSdkMock } from '../../mocks/iam/auth.mock';

type AuthMockAction = 'login' | 'logout' | 'refreshToken' | 'validateToken';

@Injectable({ providedIn: 'root' })
export class SessionsPortAdapter implements SessionsPort {
    constructor(
        private authApi: IamSdk.AuthenticationService,
        private appEvents: AppEventsService,
        private sessionCtl: SessionCtl,
        private sessionEvents: SessionEventsService,
        @Inject(KLAB_IAM_ENV) private env: EnvMicroKlamIam,
        @Inject(ROLES_PORT) private rolesPort: RolesPort,
    ) { }

    login(payload: LoginRequest): Observable<LoginResponse> {
        this.appEvents.loaderVisiblePublish(true, 'auth-login');

        const source$: Observable<IamSdk.LoginResponse> = this.shouldMock('login')
            ? of(authSdkMock.login(payload) as any).pipe(delay(450))
            : this.authApi.login(payload as IamSdk.LoginRequest);

        return source$.pipe(
            map((resp: IamSdk.LoginResponse) => mapSDKLoginToDomain(resp)),
            switchMap((session) => {
                this.sessionCtl.sessionSet(session);
                this.sessionEvents.loggedInCheckPublish('login');

                const roleId = session.user?.role?.role_id;
                // ERP Identity todavía no expone el catálogo de permisos ni el menú
                // del Klab IAM. Un menú vacío identifica ese contrato y evita una
                // consulta incompatible a /roles/{id}; Customer conserva su sesión
                // y las rutas propias siguen protegidas por el JWT del Gateway.
                if (!roleId || session.menu.length === 0) return of(session);

                return this.rolesPort.getRoleById(roleId).pipe(
                    tap((roleDetail) => {
                        const permissions: RolePermissions = {
                            allowRead: roleDetail.allowRead,
                            allowCreate: roleDetail.allowCreate,
                            allowUpdate: roleDetail.allowUpdate,
                            allowDelete: roleDetail.allowDelete,
                        };
                        this.sessionCtl.setRolePermissions(permissions);
                    }),
                    map(() => session),
                    catchError(() => of(session)),
                );
            }),
            catchError((err) => {
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'auth-login')),
        );
    }

    logout(): Observable<void> {
        this.appEvents.loaderVisiblePublish(true, 'auth-logout');

        const source$: Observable<void> = this.shouldMock('logout')
            ? of(void 0).pipe(delay(250))
            : this.authApi.logout().pipe(map(() => void 0));

        return source$.pipe(
            tap(() => {
                this.sessionCtl.sessionClear();
                this.sessionEvents.loggedInCheckPublish('logout');
            }),
            catchError(() => {
                this.sessionCtl.sessionClear();
                this.sessionEvents.loggedInCheckPublish('logout');
                return of(void 0);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'auth-logout')),
        );
    }

    getCurrentSession(): LoginResponse | undefined {
        return this.sessionCtl.sessionItem();
    }

    refreshCurrentSession(): Observable<LoginResponse> {
        const current = this.sessionCtl.sessionItem();
        if (!current) {
            return throwError(() => new Error('No active session to refresh'));
        }
        if (current.menu.length === 0 && !this.env.validateEmptyMenu) {
            return of(current);
        }

        this.appEvents.loaderVisiblePublish(true, 'auth-refresh');

        const source$: Observable<IamSdk.TokenValidationResponse> = this.shouldMock('validateToken')
            ? of(authSdkMock.validateToken?.() as IamSdk.TokenValidationResponse).pipe(delay(250))
            : this.authApi.validateToken();

        return source$.pipe(
            map((resp) => {
                const updated: LoginResponse = {
                    ...current,
                    user: mapSDKAuthenticatedUserToDomain(resp?.data?.user),
                    menu: (resp?.data?.menu ?? []).map(mapSDKMenuItemToDomain),
                };
                this.sessionCtl.sessionSet(updated);
                return updated;
            }),
            catchError((err) => throwError(() => err)),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'auth-refresh')),
        );
    }

    private shouldMock(action: AuthMockAction): boolean {
        return Boolean(this.env?.useMocks || this.env?.mock?.auth?.[action]);
    }
}
